import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import { useDispatch } from 'react-redux';
import { setMeasuring } from '../../store/reducers/helpersReducer';
import { useMapEvents } from 'react-leaflet';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Box } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Paper } from '@mui/material';

const MeasureControl = ({
    position = 'topright',
    color = '#00B6FF',
    lineWeight = 4
}) => {
    const map = useMap();
    const dispatch = useDispatch();
    const [hide, setHide] = useState(false);
    const [isMeasuring, setIsMeasuring] = useState(false);
    const pointsRef = useRef([]);
    const totalDistanceRef = useRef(0);
    const tempLineRef = useRef(null);
    const markersRef = useRef([]);
    const labelsRef = useRef([]);
    const [popup, setPopup] = useState(null);
    const popupsRef = useRef([]);
    const drawnLayersRef = useRef(new L.FeatureGroup());

    useMapEvents({
        'signal:drawing': (e) => {
            if (e.source !== 'measure') {
                setHide(e.state)
            }
        }
    });

    useEffect(() => {
        if (isMeasuring) {
            map.fire('signal:drawing', { source: 'measure', state: true })
        } else {
            map.fire('signal:drawing', { source: 'measure', state: false })
        }
    }, [isMeasuring]);

    // Initialize drawn layers group
    useEffect(() => {
        map.addLayer(drawnLayersRef.current);

        return () => {
            if (map.hasLayer(drawnLayersRef.current)) {
                map.removeLayer(drawnLayersRef.current);
            }
        };
    }, [map]);

    // Add effect to handle measurement mode changes
    useEffect(() => {
        if (isMeasuring) {
            console.log('Measurement mode activated, adding event handlers');
            map.getContainer().style.cursor = 'crosshair';
            map.on('click', handleClick);
            map.on('mousemove', handleMouseMove);
            map.on('dblclick', handleDoubleClick);
        } else {
            console.log('Measurement mode deactivated, removing event handlers');
            map.getContainer().style.cursor = '';
            map.off('click', handleClick);
            map.off('mousemove', handleMouseMove);
            map.off('dblclick', handleDoubleClick);
        }

        return () => {
            map.off('click', handleClick);
            map.off('mousemove', handleMouseMove);
            map.off('dblclick', handleDoubleClick);
        };
    }, [isMeasuring]);

    const startMeasuring = () => {
        console.log('Starting measurement mode');
        if (isMeasuring) {
            console.log('Already measuring, stopping...');
            stopMeasuring();
            return;
        }

        // dispatch(setMeasuring(true));
        setIsMeasuring(true);
        pointsRef.current = [];
        totalDistanceRef.current = 0;
        markersRef.current = [];
        labelsRef.current = [];
        popupsRef.current = [];
        setPopup(null);
        setTimeout(() => {
            map.fire('draw:drawstart');
        }, 100);
    };

    const stopMeasuring = () => {
        console.log('Stopping measurement mode');
        
        setIsMeasuring(false);
        // dispatch(setMeasuring(false));
        setTimeout(() => {
            map.fire('draw:drawstop');
        }, 100);
        // Remove all markers and lines
        markersRef.current.forEach(marker => {
            if (marker && map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
        });
        labelsRef.current.forEach(label => {
            if (label && map.hasLayer(label)) {
                map.removeLayer(label);
            }
        });

        if (tempLineRef.current && map.hasLayer(tempLineRef.current)) {
            map.removeLayer(tempLineRef.current);
            tempLineRef.current = null;
        }

        // Remove all popups
        popupsRef.current.forEach(popup => {
            if (popup && map.hasLayer(popup)) {
                map.closePopup(popup);
            }
        });
        popupsRef.current = [];

        if (popup) {
            map.closePopup(popup);
            setPopup(null);
        }

        // Clear drawn layers
        drawnLayersRef.current.clearLayers();

        pointsRef.current = [];
        totalDistanceRef.current = 0;
        markersRef.current = [];
        labelsRef.current = [];
    };

    const calculateDistance = (latlng1, latlng2) => {
        const R = 6378137;
        const dLat = (latlng2.lat - latlng1.lat) * Math.PI / 180;
        const dLon = (latlng2.lng - latlng1.lng) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(latlng1.lat * Math.PI / 180) * Math.cos(latlng2.lat * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const calculateArea = (points) => {
        if (points.length < 3) return 0;

        let area = 0;
        const R = 6378137;

        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += (points[j].lng - points[i].lng) *
                (2 + Math.sin(points[i].lat * Math.PI / 180) +
                    Math.sin(points[j].lat * Math.PI / 180));
        }

        return Math.abs(area * R * R / 2);
    };

    const formatDistance = (meters) => {
        if (meters < 1000) {
            return `${Math.round(meters)} м`;
        }
        return `${(meters / 1000).toFixed(2)} км`;
    };

    const formatArea = (squareMeters) => {
        if (squareMeters < 1000000) {
            return `${Math.round(squareMeters)} м²`;
        }
        return `${(squareMeters / 1000000).toFixed(2)} км²`;
    };

    const isPointsClose = (point1, point2, threshold = 10) => {
        return L.latLng(point1).distanceTo(L.latLng(point2)) < threshold;
    };

    const handleClick = (e) => {
        console.log('Map clicked, isMeasuring:', isMeasuring);
        if (!isMeasuring) return;

        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);

        if (pointsRef.current.length > 1 && pointsRef.current[pointsRef.current.length - 1].equals(pointsRef.current[0])) {
            pointsRef.current = [];
            popupsRef.current.forEach(popup => {
                if (popup && map.hasLayer(popup)) {
                    map.closePopup(popup);
                }
            });
            popupsRef.current = [];
            markersRef.current.forEach(marker => {
                if (marker && map.hasLayer(marker)) {
                    map.removeLayer(marker);
                }
            });
            markersRef.current = [];
        }

        const latlng = pointsRef.current.length > 2 && isPointsClose(e.latlng, pointsRef.current[0]) ? pointsRef.current[0] : e.latlng;
        console.log('Adding point at:', latlng);
        pointsRef.current.push(latlng);

        // Add point marker using leaflet-draw style
        const marker = L.circleMarker(latlng, {
            radius: 4,
            fillColor: color,
            color: color,
            weight: 8,
            opacity: 0.4,
            fillOpacity: 1
        }).addTo(map);
        markersRef.current.push(marker);
        drawnLayersRef.current.addLayer(marker);

        // If this is the first point, just add it
        if (pointsRef.current.length === 1) {
            console.log('First point added');
            return;
        }

        // Check if we're closing the polygon
        if (pointsRef.current.length > 2 && latlng.equals(pointsRef.current[0])) {
            console.log('Closing polygon');

            // Create polygon using leaflet-draw style
            const polygon = L.polygon(pointsRef.current, {
                color: color,
                weight: lineWeight,
                opacity: 0.8,
                fillOpacity: 0.3
            }).addTo(map);
            markersRef.current.push(polygon);
            drawnLayersRef.current.addLayer(polygon);

            // Calculate and show area
            const area = calculateArea(pointsRef.current);

            // Calculate center of the polygon
            const bounds = polygon.getBounds();
            const center = bounds.getCenter();

            const popupContent = `
                <div style="text-align: center;">
                    <div style="margin-bottom: 4px; font-size: 14px;">
                        Площадь: ${formatArea(area)}
                    </div>
                    <button 
                        onclick="document.dispatchEvent(new CustomEvent('finish-measurement'))"
                        style="
                            background-color: #4CAF50;
                            color: white;
                            padding: 4px 8px;
                            border: none;
                            border-radius: 4px;
                            cursor: pointer;
                            font-size: 14px;
                        "
                    >
                        Завершить измерение
                    </button>
                </div>
            `;

            const newPopup = L.popup({
                closeButton: false,
                autoClose: false,
                closeOnClick: false
            })
                .setLatLng(center)
                .setContent(popupContent)
                .addTo(map);
            popupsRef.current.push(newPopup);
            setPopup(newPopup);

            document.addEventListener('finish-measurement', stopMeasuring);
        }

        // Add line between points using leaflet-draw style
        const line = L.polyline([pointsRef.current[pointsRef.current.length - 2], latlng], {
            color: color,
            weight: lineWeight,
            opacity: 0.8
        }).addTo(map);
        markersRef.current.push(line);
        drawnLayersRef.current.addLayer(line);

        // Calculate distances
        const lastPoint = pointsRef.current[pointsRef.current.length - 2];

        const lastSegmentDistance = calculateDistance(lastPoint, latlng);
        totalDistanceRef.current += lastSegmentDistance;

        // Update popup for the previous point (without finish button)
        if (pointsRef.current.length > 2) {
            const prevPoint = pointsRef.current[pointsRef.current.length - 2];
            const prevSegmentDistance = calculateDistance(pointsRef.current[pointsRef.current.length - 3], lastPoint);

            const prevPopupContent = `
                <div style="text-align: center;">
                    <div style="margin-bottom: 4px; font-size: 14px;">
                        Участок ${pointsRef.current.length - 2}: ${formatDistance(prevSegmentDistance)}<br>
                        Общая длина: ${formatDistance(totalDistanceRef.current - lastSegmentDistance)}
                    </div>
                </div>
            `;

            // Find and update existing popup for the previous point
            const existingPopup = popupsRef.current.find(p =>
                p && p.getLatLng() &&
                p.getLatLng().equals(prevPoint)
            );

            if (existingPopup) {
                existingPopup.setContent(prevPopupContent);
            } else {
                const prevPopup = L.popup({
                    closeButton: false,
                    autoClose: false,
                    closeOnClick: false
                })
                    .setLatLng(prevPoint)
                    .setContent(prevPopupContent)
                    .addTo(map);
                popupsRef.current.push(prevPopup);
            }
        }

        // Create popup for the current point (without finish button)
        const popupContent = `
            <div style="text-align: center;">
                <div style="margin-bottom: 4px; font-size: 14px;">
                    Последний участок: ${formatDistance(lastSegmentDistance)}<br>
                    Общая длина: ${formatDistance(totalDistanceRef.current)}
                </div>
                ${!latlng.equals(pointsRef.current[0]) ? `
                    <button 
                        onclick="document.dispatchEvent(new CustomEvent('finish-measurement'))"
                        style="
                            background-color: #4CAF50;
                            color: white;
                            padding: 4px 8px;
                            border: none;
                            border-radius: 4px;
                            cursor: pointer;
                            font-size: 14px;
                        "
                    >
                        Завершить измерение
                    </button>
                ` : ''}
            </div>
        `;
        document.addEventListener('finish-measurement', stopMeasuring)

        // Remove only the last popup if it exists
        if (popup) {
            map.closePopup(popup);
            setPopup(null);
        }

        const newPopup = L.popup({
            closeButton: false,
            autoClose: false,
            closeOnClick: false
        })
            .setLatLng(latlng)
            .setContent(popupContent)
            .addTo(map);
        popupsRef.current.push(newPopup);
        setPopup(newPopup);
    };

    const handleMouseMove = (e) => {
        if (!isMeasuring || pointsRef.current.length === 0) return;

        const latlng = e.latlng;
        if (tempLineRef.current) {
            map.removeLayer(tempLineRef.current);
        }

        // Create temp line using leaflet-draw style
        tempLineRef.current = L.polyline([pointsRef.current[pointsRef.current.length - 1], latlng], {
            color: color,
            weight: lineWeight,
            opacity: 0.5,
            dashArray: '5, 5'
        }).addTo(map);
    };

    const handleDoubleClick = (e) => {
        if (!isMeasuring) return;
        e.originalEvent.stopPropagation();
        stopMeasuring();
    };

    useEffect(() => {
        return () => {
            document.removeEventListener('finish-measurement', stopMeasuring);
            stopMeasuring();
        };
    }, []);

    if (hide) {
        return null;
    }

    return createPortal(
        <Box sx={{ zIndex: 1202, display: 'flex', order: 2, pointerEvents: 'auto', width: 'fit-content' }}>
            <Tooltip title={isMeasuring ? 'Завершить измерение' : 'Начать измерение'} placement="right" arrow>
                <Paper
                    elevation={3}
                    sx={{
                        display: 'flex',
                        order: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: window.innerWidth < 768 ? 26 : 30,
                        height: window.innerWidth < 768 ? 26 : 30,
                        opacity: 0.9,
                        borderRadius: '5px',
                        bgcolor: 'rgb(40, 40, 40)',
                        boxShadow: isMeasuring ? '0 0 3px 3px rgb(0, 195, 255)' : '0px 2px 6px grey',
                        color: isMeasuring ? 'red' : 'white',
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        '&:hover': {
                            bgcolor: 'rgb(50, 50, 50)',
                            transform: 'scale(1.1)',

                        },
                        // zoom: window.innerWidth < 768 ? 0.8 : 1
                    }}
                    onClick={() => {
                        if (isMeasuring) {
                            stopMeasuring();
                        } else {
                            startMeasuring();
                        }
                    }}
                >
                    {!isMeasuring ? <SquareFootIcon sx={{ fontSize: window.innerWidth < 768 ? 20 : 24, color: 'white' }}/> : <CloseOutlinedIcon sx={{ fontSize: window.innerWidth < 768 ? 20 : 24, color: 'white' }}/>}
                </Paper>
            </Tooltip>
        </Box>

        , document.getElementById(window.innerWidth < 768 ? "map-controls-left-bottom-first-row-left" : "map-controls-left-bottom")
    )

};

export default MeasureControl;
